import React, { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { sendChatMessage } from '../services/chatService'
import './ChatBot.css'

const SUGGESTIONS = [
  'Starlink installation',
  'CCTV & security',
  'Browse the shop',
  'Talk to a human',
]

const WELCOME =
  'Hello — I am the Fabritech Assistant. I can help with Starlink, CCTV & security, software projects, training, shop products, payments, delivery, and connecting you to our team.'

const HIDDEN_PATHS = ['/dashboard', '/login', '/signup', '/checkout']

function formatTime(date) {
  try {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export default function ChatBot() {
  const location = useLocation()
  const titleId = useId()
  const listRef = useRef(null)
  const inputRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [suggestions, setSuggestions] = useState(SUGGESTIONS)
  const [messages, setMessages] = useState(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content: WELCOME,
      at: new Date(),
    },
  ])

  const hidden = HIDDEN_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  )

  useEffect(() => {
    if (!open || !listRef.current) return
    listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages, loading, open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const t = window.setTimeout(() => inputRef.current?.focus(), 120)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
    }
  }, [open])

  if (hidden) return null

  const send = async (rawText) => {
    const text = String(rawText || '').trim()
    if (!text || loading) return

    const userMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
      at: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setError('')
    setLoading(true)

    try {
      const history = [...messages, userMsg]
        .filter((m) => m.id !== 'welcome')
        .map(({ role, content }) => ({ role, content }))

      const data = await sendChatMessage(text, history)
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: data.reply,
          at: new Date(),
        },
      ])
      if (Array.isArray(data.suggestions) && data.suggestions.length) {
        setSuggestions(data.suggestions.slice(0, 4))
      }
    } catch (err) {
      setError(err.message || 'Something went wrong')
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content:
            'I could not reach the assistant service just now. Please try again, or contact us at +250 788 601 280 / info@fabritech.rw.',
          at: new Date(),
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = (event) => {
    event.preventDefault()
    send(input)
  }

  return (
    <div className={`fab-chat ${open ? 'is-open' : ''}`}>
      {open ? (
        <section
          className="fab-chat__panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
        >
          <header className="fab-chat__header">
            <div className="fab-chat__brand">
              <div className="fab-chat__avatar" aria-hidden="true">
                <i className="fa-solid fa-robot" />
              </div>
              <div>
                <h2 id={titleId} className="fab-chat__title">
                  Fabritech Assistant
                </h2>
                <p className="fab-chat__status">
                  <span className="fab-chat__dot" aria-hidden="true" />
                  Online · typically replies instantly
                </p>
              </div>
            </div>
            <button
              type="button"
              className="fab-chat__icon-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </header>

          <div className="fab-chat__body" ref={listRef}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`fab-chat__bubble fab-chat__bubble--${msg.role}`}
              >
                <p>{msg.content}</p>
                <time dateTime={msg.at.toISOString()}>{formatTime(msg.at)}</time>
              </div>
            ))}

            {loading ? (
              <div className="fab-chat__bubble fab-chat__bubble--assistant fab-chat__typing" aria-live="polite">
                <span />
                <span />
                <span />
              </div>
            ) : null}

            {!loading ? (
              <div className="fab-chat__suggestions">
                {suggestions.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => send(label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {error ? (
            <p className="fab-chat__error" role="alert">
              {error}
            </p>
          ) : null}

          <form className="fab-chat__composer" onSubmit={onSubmit}>
            <label className="visually-hidden" htmlFor="fab-chat-input">
              Message
            </label>
            <input
              id="fab-chat-input"
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about products or services…"
              autoComplete="off"
              maxLength={2000}
              disabled={loading}
            />
            <button
              type="submit"
              className="fab-chat__send"
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            </button>
          </form>

          <footer className="fab-chat__footer">
            <a href="https://wa.me/250788601280" target="_blank" rel="noreferrer">
              Prefer WhatsApp?
            </a>
            <span>Powered by Fabritech AI</span>
          </footer>
        </section>
      ) : null}

      <button
        type="button"
        className={`fab-chat__launcher${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Close live chat' : 'Open live chat'}
        title="Live chat"
      >
        {open ? (
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        ) : (
          <>
            <i className="fa-regular fa-comment" aria-hidden="true" />
            <span className="fab-chat__launcher-label">Live chat</span>
          </>
        )}
      </button>
    </div>
  )
}
